// Chunked-upload helper. Uses Cloudflare R2 direct browser-to-bucket upload
// when the backend has R2 configured (preferred — no proxy in the way, no
// size cap, persists permanently). Falls back to the local chunked protocol
// for backends without R2, and to a single-shot upload for tiny files.
import axios from "axios";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SINGLE_SHOT_THRESHOLD = 50 * 1024 * 1024; // ≤50 MB → single POST when R2 is unavailable
const RETRY_PER_CHUNK = 3;

const authHeaders = () => {
  const token = localStorage.getItem("ysv_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Read a video file's duration (rounded seconds) from its metadata, 0 if the
 * browser can't decode it. The player builds its whole timeline from
 * duration_seconds, so callers must pass this instead of 0 — GDO-portal
 * uploads that recorded 0 are what broke tick marks / quarter markers.
 */
export const readVideoDuration = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Number.isFinite(v.duration) ? Math.round(v.duration) : 0); };
  v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
  v.src = url;
});

/**
 * Upload directly to Cloudflare R2 via a backend-issued presigned URL.
 * Returns the created game on success. Auto-retries on network blips and
 * auto-refreshes the URL if it expired mid-upload.
 */
export async function r2DirectUploadGame(file, meta, onProgress) {
  // 1) Ask backend for a presigned PUT URL
  const presignRes = await axios.post(`${API_ROOT}/games/upload/r2/presign`, {
    filename: file.name,
    content_type: file.type,
    total_size: file.size,
    title: meta.title,
    description: meta.description || "",
    team_id: meta.team_id,
    away_team_id: meta.away_team_id || null,
    opponent: meta.opponent || "",
    game_date: meta.game_date,
    duration_seconds: meta.duration_seconds || 0,
    segment_label: meta.segment_label || null,
    parent_game_id: meta.parent_game_id || null,
    schedule_id: meta.schedule_id || null,
  }, { headers: authHeaders() });
  const { session_id } = presignRes.data;
  let { upload_url } = presignRes.data;

  // 2) PUT the file directly to R2 — with retry + auto-refresh of presigned URL
  const MAX_ATTEMPTS = 5;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      await axios.put(upload_url, file, {
        headers: { "Content-Type": file.type },
        // Important: don't let axios add an Authorization header to R2 — it won't
        // accept it. Our backend only signs the URL with the right query params.
        transformRequest: [(d) => d],
        // Cellular uploads can take a long time; allow up to 4h per attempt.
        timeout: 4 * 60 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          // Reserve the last 2% for the backend complete-confirmation step.
          onProgress?.(Math.min(98, Math.round((evt.loaded / evt.total) * 98)));
        },
      });
      break;  // success — leave the retry loop
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.code;
      // Don't retry on permanent failures
      if (status && status !== 403 && status !== 408 && status < 500 && code !== "ECONNABORTED" && code !== "ERR_NETWORK") {
        throw err;
      }
      if (attempt >= MAX_ATTEMPTS) throw err;
      // 403 from R2 usually means the presigned URL expired — refresh it
      if (status === 403) {
        try {
          const fd = new FormData();
          fd.append("session_id", session_id);
          const r = await axios.post(`${API_ROOT}/games/upload/r2/refresh-url`, fd, { headers: authHeaders() });
          upload_url = r.data.upload_url;
        } catch (refreshErr) {
          throw refreshErr;
        }
      }
      // Exponential backoff: 2s, 4s, 8s, 16s
      const delayMs = 2000 * Math.pow(2, attempt - 1);
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }

  // 3) Tell the backend the upload finished → it creates the game record
  const fd = new FormData();
  fd.append("session_id", session_id);
  const completeRes = await axios.post(`${API_ROOT}/games/upload/r2/complete`, fd, {
    headers: authHeaders(),
  });
  onProgress?.(100);
  return completeRes.data;
}

/**
 * Chunked upload via local backend (fallback when R2 isn't configured).
 */
export async function chunkedUploadGame(file, meta, onProgress) {
  const initRes = await axios.post(`${API_ROOT}/games/upload/init`, {
    filename: file.name,
    content_type: file.type,
    total_size: file.size,
    title: meta.title,
    description: meta.description || "",
    team_id: meta.team_id,
    opponent: meta.opponent || "",
    game_date: meta.game_date,
    duration_seconds: meta.duration_seconds || 0,
  }, { headers: authHeaders() });
  const { session_id, chunk_size, total_chunks } = initRes.data;

  let bytesUploaded = 0;
  for (let i = 0; i < total_chunks; i++) {
    const start = i * chunk_size;
    const end = Math.min(start + chunk_size, file.size);
    const blob = file.slice(start, end);
    const fd = new FormData();
    fd.append("session_id", session_id);
    fd.append("chunk_index", String(i));
    fd.append("chunk", blob);
    let lastErr = null;
    for (let attempt = 0; attempt < RETRY_PER_CHUNK; attempt++) {
      try {
        const localBaseline = bytesUploaded;
        await axios.post(`${API_ROOT}/games/upload/chunk`, fd, {
          headers: authHeaders(),
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = (localBaseline + evt.loaded) / file.size;
            onProgress?.(Math.min(100, Math.round(pct * 100)));
          },
        });
        bytesUploaded = end;
        onProgress?.(Math.min(100, Math.round((bytesUploaded / file.size) * 100)));
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        if (attempt < RETRY_PER_CHUNK - 1) {
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
      }
    }
    if (lastErr) throw lastErr;
  }

  const completeFd = new FormData();
  completeFd.append("session_id", session_id);
  const res = await axios.post(`${API_ROOT}/games/upload/complete`, completeFd, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function singleShotUploadGame(file, meta, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("title", meta.title);
  fd.append("description", meta.description || "");
  fd.append("team_id", meta.team_id);
  if (meta.away_team_id) fd.append("away_team_id", meta.away_team_id);
  fd.append("opponent", meta.opponent || "");
  fd.append("game_date", meta.game_date);
  // Without these the R2-less fallback silently dropped the extracted duration
  // (0 -> broken player timeline) and never linked the GDO's schedule card.
  fd.append("duration_seconds", String(meta.duration_seconds || 0));
  if (meta.schedule_id) fd.append("schedule_id", meta.schedule_id);
  const res = await axios.post(`${API_ROOT}/games/upload`, fd, {
    headers: authHeaders(),
    onUploadProgress: (evt) => {
      if (!evt.total) return;
      onProgress?.(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return res.data;
}

export async function uploadGameAuto(file, meta, onProgress) {
  // Always try R2 first — it's the production path. Fall back to chunked
  // local upload only if the backend can't issue a presigned URL (older
  // env without R2 configured).
  try {
    return await r2DirectUploadGame(file, meta, onProgress);
  } catch (e) {
    const status = e?.response?.status;
    if (status === 503) {
      if (file.size <= SINGLE_SHOT_THRESHOLD) {
        return singleShotUploadGame(file, meta, onProgress);
      }
      return chunkedUploadGame(file, meta, onProgress);
    }
    throw e;
  }
}
